import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fetchPresence } from '../util/api';
import { capPresenceIds } from './presence';
import {
  PRESENCE_POLL_MS,
  isPresenceRateLimited,
  presenceBusyMapFromResponse,
  presenceLiveMapFromResponse,
  presenceMapFromResponse,
  presenceNextAvailableMapFromResponse,
  presencePausedMapFromResponse,
  presenceRetryAfterMs,
  shouldApplyPresenceResponse,
} from '../util/presence';
import { getPresenceIntentIds } from '../util/presenceIntent';
import { subscribeSharedPoll } from './sharedPoll';

const PRESENCE_FETCH_TIMEOUT_MS = 3000;

/**
 * Public presence for listing cards. Returns `{ map, ready, applyLocal }`.
 * Unknown listings are offline until a heartbeat says otherwise — that way
 * Online now can render an empty state on SSR instead of hanging on
 * “Checking who is available now…”.
 *
 * In-flight GETs are ignored after a newer request or a local Available now
 * write so a stale offline payload cannot roll the dashboard back.
 */
const usePresenceMap = (listingIds, { includeAllOnline = true } = {}) => {
  const idsKey = capPresenceIds(listingIds).join(',');
  const ids = useMemo(() => idsKey.split(',').filter(Boolean), [idsKey]);
  const [map, setMap] = useState({});
  const [pausedMap, setPausedMap] = useState({});
  const [busyMap, setBusyMap] = useState({});
  const [liveMap, setLiveMap] = useState({});
  const [nextAvailableMap, setNextAvailableMap] = useState({});
  const [ready, setReady] = useState(true);
  const requestTokenRef = useRef(0);

  const apply = useCallback((nextMap, token, extras = {}) => {
    if (!shouldApplyPresenceResponse(token, requestTokenRef.current)) {
      return;
    }
    const intended = getPresenceIntentIds();
    const merged = { ...nextMap };
    intended.forEach(listingId => {
      merged[listingId] = true;
    });
    setMap(merged);
    setPausedMap(extras.paused || {});
    setBusyMap(extras.busy || {});
    const live = { ...(extras.live || {}) };
    intended.forEach(listingId => {
      live[listingId] = true;
    });
    setLiveMap(live);
    setNextAvailableMap(extras.nextAvailable || {});
    setReady(true);
  }, {});

  const applyLocal = useCallback((listingId, online) => {
    if (!listingId) {
      return;
    }
    requestTokenRef.current += 1;
    setMap(prev => ({ ...prev, [listingId]: !!online }));
    setLiveMap(prev => ({ ...prev, [listingId]: !!online }));
    setReady(true);
  }, []);

  useEffect(() => {
    if (ids.length === 0) {
      requestTokenRef.current += 1;
      setMap({});
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    let firstDone = false;
    const firstToken = (requestTokenRef.current += 1);
    const timeout =
      process.env.NODE_ENV === 'test' || typeof window === 'undefined'
        ? null
        : window.setTimeout(() => {
            if (!firstDone && !cancelled) {
              firstDone = true;
              apply({}, firstToken);
            }
          }, PRESENCE_FETCH_TIMEOUT_MS);
    const stop =
      process.env.NODE_ENV === 'test' || typeof window === 'undefined'
        ? () => {}
        : subscribeSharedPoll({
            key: `presence:${includeAllOnline ? 'all' : 'ids'}:${idsKey}`,
            intervalMs: PRESENCE_POLL_MS,
            skipWhenHidden: true,
            shouldBackoff: isPresenceRateLimited,
            nextDelayMs: error => presenceRetryAfterMs(error),
            fetchFn: () => fetchPresence(ids),
            onData: (error, payload) => {
              if (cancelled) {
                return;
              }
              const token = (requestTokenRef.current += 1);
              if (error) {
                if (!firstDone) {
                  firstDone = true;
                  apply({}, token);
                }
                return;
              }
              firstDone = true;
              apply(presenceMapFromResponse(payload), token, {
                paused: presencePausedMapFromResponse(payload),
                busy: presenceBusyMapFromResponse(payload),
                live: presenceLiveMapFromResponse(payload),
                nextAvailable: presenceNextAvailableMapFromResponse(payload),
              });
            },
          });
    return () => {
      cancelled = true;
      if (timeout) {
        window.clearTimeout(timeout);
      }
      stop();
    };
  }, [ids, apply, includeAllOnline]);

  return { map, pausedMap, busyMap, liveMap, nextAvailableMap, ready, applyLocal };
};

export default usePresenceMap;
